import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SVGIcon as Icon } from 'common/components';
import { useClassName } from 'common/hooks';
import {
  useSearchSuggestions,
  useClickOutside,
  useKeyboardNavigation,
} from '../hooks';
import './styles/search-input.less';

const SearchInput = ({ input, setInput, placeholder, events }) => {
  const classNames = useClassName('SearchInput');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const suggestions = useSearchSuggestions(input, events);

  useEffect(() => {
    if (suggestions.length > 0 && input !== suggestions[0]) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    setSelectedIndex(-1);
  }, [input, suggestions]);

  useClickOutside(wrapperRef, () => {
    setIsOpen(false);
    setSelectedIndex(-1);
  });

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = useKeyboardNavigation({
    suggestions,
    isOpen,
    selectedIndex,
    setIsOpen,
    setSelectedIndex,
    handleSuggestionClick,
    inputRef,
  });

  const handleClearInput = () => {
    setInput('');
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const renderSuggestion = (suggestion) => {
    if (!input) return suggestion;

    const index = suggestion.toLowerCase().indexOf(input.toLowerCase());
    if (index === -1) return suggestion;

    const before = suggestion.slice(0, index);
    const match = suggestion.slice(index, index + input.length);
    const after = suggestion.slice(index + input.length);

    return (
      <>
        <span className={classNames('suggestion-text-muted')}>{before}</span>
        <span className={classNames('suggestion-text-matched')}>{match}</span>
        <span className={classNames('suggestion-text-muted')}>{after}</span>
      </>
    );
  };

  return (
    <div className={classNames('container')} ref={wrapperRef}>
      <div className={classNames('input-wrapper')}>
        <span className={classNames('search-icon')}>
          <Icon name="search" size="tiny" />
        </span>
        <div className={classNames('search-input')}>
          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            value={input}
            aria-expanded={isOpen}
            aria-controls="search-suggestions"
            aria-activedescendant={
              selectedIndex >= 0 ? `suggestion-${selectedIndex}` : undefined
            }
            role="combobox"
          />
          {input && (
            <button
              className={classNames('clear-button')}
              onClick={handleClearInput}
              type="button"
              aria-label="Clear search">
              <Icon name="close" size="mini" />
            </button>
          )}
        </div>
      </div>

      {isOpen && suggestions.length > 0 && (
        <div
          id="search-suggestions"
          className={classNames('suggestions-dropdown')}
          role="listbox">
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion}
              id={`suggestion-${index}`}
              className={classNames([
                'suggestion-item',
                index === selectedIndex && 'selected',
              ])}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
              role="option"
              aria-selected={index === selectedIndex}>
              {renderSuggestion(suggestion)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

SearchInput.propTypes = {
  input: PropTypes.string.isRequired,
  setInput: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  events: PropTypes.array,
};

SearchInput.defaultProps = {
  placeholder: 'Search',
  events: [],
};

export default SearchInput;
